// « Mains vives » (D-163, D-164) : ce que l'app demande à la base. La vidéo et l'image ne quittent jamais le téléphone : la
// base reçoit seulement, à la fin d'une séance, la durée, le niveau et les trois chiffres (mv_session_save). La base fait le
// tri : les chiffres se lisent par le joueur lui-même et le parent d'un mineur (mv_sessions) ; la proposition du coach par
// l'équipe (mv_proposals) ; le suivi (qui l'a fait, combien de fois) par le staff de l'équipe (mv_follow).
// D-165 : les défis Mains vives ouverts d'une personne (mv_challenges_for) ; « Envoyer mon score au défi » envoie le score
// de SA séance (mv_challenge_send : le joueur adulte, ou le parent qui a lancé la séance de son enfant mineur).
import { supabase } from './supabase';
import type { MvMeasure } from '../domain/challenges';
import type { Level, SessionRow, Summary } from '../domain/mains-vives';
import { keptFigures } from '../domain/mains-vives';

const db = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};
async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await db().rpc(fn, args);
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return data as T;
}

export interface MvProposal {
  id: string; team_id: string; times: number; due_on: string; level: Level; note: string | null; author_name: string | null; created_at: string; closed_at: string | null;
}
export interface MvFollow { person_id: string; name: string; done: number }

/** Les séances d'une personne (les miennes, ou celles de mon enfant mineur), de la plus récente à la plus ancienne. */
export async function mvSessions(personId: string, limit = 120): Promise<SessionRow[]> {
  const { data, error } = await db().from('mv_sessions').select('played_at, duration_s, level, hits, avg_ms, streak')
    .eq('person_id', personId).order('played_at', { ascending: false }).limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as SessionRow[];
}

/** La proposition en cours du coach pour ces équipes (la plus récente, pas fermée, pas échue). */
export async function mvProposals(teamIds: string[], today: string): Promise<MvProposal[]> {
  if (!teamIds.length) return [];
  const { data, error } = await db().from('mv_proposals').select('id, team_id, times, due_on, level, note, author_name, created_at, closed_at')
    .in('team_id', teamIds).is('closed_at', null).gte('due_on', today).order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as MvProposal[];
}

/** Enregistre une séance finie : la durée, le niveau et les trois chiffres, rien d'autre. */
export const mvSave = (personId: string, s: Summary) => {
  const k = keptFigures(s);
  return rpc<string>('mv_session_save', { p_person: personId, p_duration_s: k.duration_s, p_level: k.level, p_hits: k.hits, p_avg_ms: k.avg_ms, p_streak: k.streak });
};

/** Un défi Mains vives ouvert, où la personne peut envoyer son score (D-165). */
export interface MvChallenge { id: string; team_id: string; title: string; mv_level: Level; mv_measure: MvMeasure; mv_duration_s: number; ends_on: string | null; created_at: string }
export const mvChallengesFor = async (personId: string) => (await rpc<MvChallenge[] | null>('mv_challenges_for', { p_person: personId })) ?? [];
/** Envoie le score d'une séance (la sienne) à un défi Mains vives ; rend l'identifiant du résultat. */
export const mvChallengeSend = (sessionId: string, challengeId: string) => rpc<string>('mv_challenge_send', { p_session: sessionId, p_challenge: challengeId });

/** Le coach propose le drill à son équipe. */
export const mvPropose = (teamId: string, times: number, due: string, level: Level, note: string) =>
  rpc<string>('mv_propose', { p_team: teamId, p_times: times, p_due: due, p_level: level, p_note: note });

/** Qui l'a fait, et combien de fois (le staff de l'équipe). */
export const mvFollow = (proposalId: string) => rpc<MvFollow[]>('mv_follow', { p_proposal: proposalId });
